import { Theme } from '@mui/material/styles';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useBoolean } from 'usehooks-ts';

import { Divider } from '@/components/Layout/Divider';
import { DrawerProps } from '@/components/Layout/Drawer';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs';
import { selectUserData } from '@/redux/user-data/selectors';

import {
  Document,
  DocumentSection,
  DocumentStatus,
  RequiredDocument,
  RequiredDocumentSection,
} from '@/types/document';
import { EmploymentStatus, UserStatus } from '@/types/user';

import { getDataTestId } from '@/utils/browser';
import { isEmploymentInfoCollected } from '@/utils/user';

import { RequiredDocumentGroup } from '../../RequiredDocuments/RequiredDocumentGroup';
import { EmploymentInfoDrawer } from '../EmploymentInfo';
import { EmploymentStartUpInfoDrawer } from '../EmploymentInfo/EmploymentStartupInfoDrawer';
import { RequiredDocumentUploadDrawer } from '../RequiredDocumentUploadDrawer';
import { ViewDocumentDrawer } from '../ViewDocumentDrawer';

import { StyledRequiredDocumentSelectDrawer } from '../Drawers.styled';

type POEDocSelectDrawerProps = {
  section: RequiredDocumentSection;
  additionalSections?: RequiredDocumentSection[];
} & DrawerProps;

export function POEDocSelectDrawer({
  section,
  additionalSections,
  onClose,
  ...props
}: POEDocSelectDrawerProps) {
  const { t } = useTranslate();
  const { user, documentByDocType, requiredDocumentSections } =
    useAppSelector(selectUserData);
  const {
    value: isOpenUploadDrawer,
    setTrue: openUploadDrawer,
    setFalse: closeUploadDrawer,
  } = useBoolean(false);
  const {
    value: isOpenViewDrawer,
    setTrue: openViewDrawer,
    setFalse: closeViewDrawer,
  } = useBoolean(false);
  const { showSelfEmployedStartupQuestion } =
    useAppSelector(selectConfigsState);
  const [docToUpload, setDocToUpload] = useState<RequiredDocument>();
  const [docToView, setDocToView] = useState<Document>();

  const proofOfEmploymentSection = requiredDocumentSections.find(
    section => section.type === DocumentSection.ProofOfEmployment,
  );

  const isEmploymentQuestionsAnswered = useMemo(
    () => (user ? isEmploymentInfoCollected(user) : false),
    [user],
  );

  const shouldShowStartUpQuestion = useMemo(() => {
    if (!user) return false;

    const isDraftOrArchivedOrInReview = [
      UserStatus.Draft,
      UserStatus.Archived,
      UserStatus.InReview,
    ].includes(user?.status);

    if (!isDraftOrArchivedOrInReview) return false;

    const employment = user?.employmentDetailed;

    return (
      showSelfEmployedStartupQuestion &&
      employment?.employmentStatus === EmploymentStatus.SelfEmployed &&
      !employment?.riskSectorFlag &&
      employment?.isStartup === undefined
    );
  }, [user, showSelfEmployedStartupQuestion]);

  const isEmploymentFlowComplete =
    section.type === DocumentSection.ProofOfEmployment &&
    isEmploymentQuestionsAnswered &&
    !section.docs?.length &&
    !shouldShowStartUpQuestion;

  useEffect(() => {
    if (props.open && isEmploymentFlowComplete) {
      onClose?.();
    }
  }, [props.open, isEmploymentFlowComplete, onClose]);

  if (
    section.type === DocumentSection.ProofOfEmployment &&
    !isEmploymentQuestionsAnswered &&
    props.open
  ) {
    return (
      <EmploymentInfoDrawer
        {...props}
        title={t(section.displayText || section.type)}
        onClose={onClose}
      />
    );
  }

  if (
    section.type === DocumentSection.ProofOfEmployment &&
    shouldShowStartUpQuestion
  ) {
    return (
      <EmploymentStartUpInfoDrawer
        {...props}
        title={t(section.displayText || section.type)}
        onClose={onClose}
      />
    );
  }

  if (!proofOfEmploymentSection || isEmploymentFlowComplete) return null;

  const hideUploader = () => {
    closeUploadDrawer();
    setDocToUpload(undefined);
  };

  const hideViewer = () => {
    closeViewDrawer();
    setDocToView(undefined);
  };

  const onSelectDocType = (doc: RequiredDocument) => {
    const docKey = doc.groupedAs || doc.name;
    const targetSectionType = String(proofOfEmploymentSection.type);
    const docToView = documentByDocType[targetSectionType]?.[docKey];
    setDocToUpload(doc);
    if (doc.status === DocumentStatus.Pending) {
      openUploadDrawer();
    } else {
      setDocToView(docToView);
      openViewDrawer();
    }
  };

  return (
    <>
      <StyledRequiredDocumentSelectDrawer
        {...props}
        {...getDataTestId(`${section}-required-document-select-drawer`)}
        onClose={onClose}
        title={t(section.displayText || section.type)}
        bgColor={(theme: Theme) => theme.palette.grey[100]}
        headerBgColor={(theme: Theme) => theme.palette.grey[100]}
      >
        {
          <>
            {(proofOfEmploymentSection.groups || []).map((group, index) => (
              <Fragment key={index}>
                <RequiredDocumentGroup
                  group={group}
                  onItemSelect={doc => onSelectDocType(doc)}
                />
                {index <
                  (!proofOfEmploymentSection.groups
                    ? 0
                    : proofOfEmploymentSection.groups.length - 1) && (
                  <Divider
                    width="100%"
                    containerProps={{
                      my: 4,
                    }}
                  />
                )}
              </Fragment>
            ))}
          </>
        }
      </StyledRequiredDocumentSelectDrawer>
      {/* #region Next step drawer */}
      <RequiredDocumentUploadDrawer
        open={isOpenUploadDrawer}
        section={DocumentSection.ProofOfEmployment}
        type={docToUpload?.groupedAs || docToUpload?.name}
        onClose={closeUploadDrawer}
        onUploaded={hideUploader}
        title={t(section.displayText || section.type)}
      />
      <ViewDocumentDrawer
        open={isOpenViewDrawer}
        section={proofOfEmploymentSection}
        document={docToView}
        onClose={hideViewer}
        title={t('documentSection.Proof_of_Employment.title')}
      />
      {/* #endregion */}
    </>
  );
}
